/**
 * Contrato HTTP da base de conhecimento da IA.
 *
 * A empresa cadastra fontes (site, PDF, perguntas frequentes, tabela de
 * preços, texto livre e roteiros aprendidos); o worker quebra cada uma em
 * pedaços, e o agente de IA dos fluxos responde dúvidas só com eles.
 *
 * FAQ e preços viajam como LISTA estruturada, e não texto: o editor da tela
 * reabre linha por linha, e o worker quebra uma pergunta (ou um grupo de
 * preços) por pedaço — que é o que faz a busca achar a resposta certa.
 */
import { z } from "zod";
export const KNOWLEDGE_KINDS = ["text", "faq", "website", "file", "prices", "playbook"];
export const KNOWLEDGE_KIND_LABEL = {
    text: "Texto livre",
    faq: "Perguntas frequentes",
    website: "Site",
    file: "Arquivo",
    prices: "Tabela de preços",
    playbook: "Roteiro aprendido",
};
export const KNOWLEDGE_STATUSES = ["pending", "processing", "ready", "failed"];
export const KNOWLEDGE_STATUS_LABEL = {
    pending: "Na fila",
    processing: "Lendo",
    ready: "Pronto",
    failed: "Falhou",
};
/** Limites que a tela e o servidor dividem. */
export const KNOWLEDGE_LIMITS = {
    /** Texto livre / roteiro. */
    bodyMaxChars: 100_000,
    faqMaxItems: 300,
    pricesMaxItems: 500,
    fileMaxBytes: 10 * 1024 * 1024,
    fileMaxPages: 200,
    websiteMaxPages: 30,
    websiteMaxDepth: 2,
};
/** Tipos de arquivo aceitos no upload (o resto é recusado com mensagem clara). */
export const KNOWLEDGE_FILE_ACCEPT = [".pdf", ".txt", ".md"];
export const knowledgeFaqItemSchema = z.object({
    question: z.string().trim().min(1, "Escreva a pergunta").max(500),
    answer: z.string().trim().min(1, "Escreva a resposta").max(4000),
});
export const knowledgePriceItemSchema = z.object({
    item: z.string().trim().min(1, "Escreva o item").max(200),
    price: z.string().trim().min(1, "Escreva o preço").max(100),
    notes: z.string().trim().max(500).optional(),
});
const titleSchema = z.string().trim().min(1, "Dê um nome").max(200);
/**
 * Criação por JSON. Arquivo vai por multipart na mesma rota (campo `file`,
 * título opcional em `?title=`).
 */
export const createKnowledgeSourceSchema = z.discriminatedUnion("kind", [
    z.object({
        kind: z.literal("text"),
        title: titleSchema,
        body: z.string().trim().min(1, "Escreva o texto").max(KNOWLEDGE_LIMITS.bodyMaxChars),
    }),
    z.object({
        kind: z.literal("playbook"),
        title: titleSchema,
        body: z.string().trim().min(1, "Escreva o roteiro").max(KNOWLEDGE_LIMITS.bodyMaxChars),
    }),
    z.object({
        kind: z.literal("faq"),
        title: titleSchema,
        items: z
            .array(knowledgeFaqItemSchema)
            .min(1, "Inclua pelo menos uma pergunta")
            .max(KNOWLEDGE_LIMITS.faqMaxItems),
    }),
    z.object({
        kind: z.literal("prices"),
        title: titleSchema,
        items: z
            .array(knowledgePriceItemSchema)
            .min(1, "Inclua pelo menos um item")
            .max(KNOWLEDGE_LIMITS.pricesMaxItems),
    }),
    z.object({
        kind: z.literal("website"),
        title: titleSchema.optional(),
        url: z
            .string()
            .trim()
            .url("Endereço inválido")
            .max(2000)
            .refine((u) => /^https?:\/\//i.test(u), "Use um endereço http:// ou https://"),
    }),
]);
/**
 * Edição. `body` para texto/roteiro; `items` para FAQ/preços (o servidor
 * confere o que vale para o tipo da fonte). Mudar o conteúdo reprocessa.
 */
export const updateKnowledgeSourceSchema = z
    .object({
    title: titleSchema.optional(),
    enabled: z.boolean().optional(),
    body: z.string().trim().min(1).max(KNOWLEDGE_LIMITS.bodyMaxChars).optional(),
    faqItems: z.array(knowledgeFaqItemSchema).min(1).max(KNOWLEDGE_LIMITS.faqMaxItems).optional(),
    priceItems: z
        .array(knowledgePriceItemSchema)
        .min(1)
        .max(KNOWLEDGE_LIMITS.pricesMaxItems)
        .optional(),
})
    .refine((v) => Object.values(v).some((x) => x !== undefined), "Nada para alterar");
export const knowledgeSearchSchema = z.object({
    query: z.string().trim().min(2, "Escreva a pergunta").max(500),
    sourceIds: z.array(z.string().uuid()).max(100).optional(),
});
export const knowledgeAskSchema = z.object({
    question: z.string().trim().min(2, "Escreva a pergunta").max(1000),
    sourceIds: z.array(z.string().uuid()).max(100).optional(),
});
/** Paginação local: importar a do `index` daria import circular no pacote. */
export const listKnowledgeChunksQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(20),
});
// ── Roteiro aprendido ("Clonar a melhor atendente") ─────────────────────────
/**
 * Limites da geração do roteiro a partir das conversas.
 *
 * O mínimo de convertidas existe porque, com menos de 8 exemplos, o "padrão"
 * que a IA enxerga é o jeito de UMA conversa — e um roteiro tirado de um caso
 * só ensina o agente a repetir um acaso, não o que funciona na clínica.
 */
export const PLAYBOOK_LIMITS = {
    periodDaysDefault: 180,
    periodDaysMin: 30,
    periodDaysMax: 365,
    minConverted: 8,
    maxConverted: 40,
    maxNotConverted: 20,
    /** Mensagens de cada lado para a conversa contar como troca de verdade. */
    minMessagesEachSide: 3,
    /** Sem resposta há mais que isso, a proposta é considerada parada. */
    stalledAfterDays: 7,
};
export const playbookPreviewSchema = z.object({
    periodDays: z.coerce
        .number()
        .int()
        .min(PLAYBOOK_LIMITS.periodDaysMin)
        .max(PLAYBOOK_LIMITS.periodDaysMax)
        .optional(),
});
/**
 * Gerar exige `confirm: true`: trechos (anonimizados) das conversas vão para
 * o provedor de IA da clínica, e isso precisa ser uma decisão explícita de
 * quem gere a empresa — não um clique distraído.
 */
export const playbookGenerateSchema = playbookPreviewSchema.extend({
    confirm: z.literal(true, {
        errorMap: () => ({ message: "Confirme que entende o envio dos trechos anonimizados" }),
    }),
});
/**
 * Configuração da base no bloco `ai_agent` (espelho de `@rn/core/flow`).
 * Exportado para o editor do bloco montar o interruptor e a seleção de fontes.
 */
export const aiAgentKnowledgeSchema = z.object({
    enabled: z.boolean(),
    sourceIds: z.array(z.string().uuid()).max(100).optional(),
});
//# sourceMappingURL=knowledge.js.map